/**
 * Matriz de riesgo — shared model, helpers and small primitives used by both
 * tabs (Configuración wizard + Simulador). Extracted from the old single-file
 * console page so each tab reads clean and one-thing-at-a-time.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Ban, Building2, Check, ChevronDown, Info, Landmark, Scale, TrendingUp, Umbrella } from 'lucide-react';
import {
  INDICATOR_APPLICABILITY,
  RECOMMENDED_RISK_MATRIX,
  RISK_FACTOR_REFERENCE,
  RISK_SUBFACTOR_CATALOGS,
  catalogForMode,
  esPA,
  sectorPresetsForMode,
  type EntityMode,
  type RiskCatalogEntry,
  type RiskFactorKey,
  type RiskMatrixConfig,
  type RiskMatrixResponse,
  type RiskNivel,
} from '@cumpli/shared';
import { THRESHOLD_HEX } from '../../lib/tokens';
import { FieldHelp, BigButton } from '../../components/flow';
import { Input, Label, Pill, cn } from '../../components/ui';

// ------------------------------------------------------------------ types ----

export type Weights = { cliente: number; jurisdiccion: number; canal: number; producto: number };
export type Thresholds = { medio: number; alto: number };
export type SubState = {
  cliente: { profesion: number; estructura: number; antecedentes: number };
  jurisdiccion: { nacionalidad: number; origen_destino: number; domicilio: number; listas: number };
  canal: { canal_via: number; frecuencia: number; monto: number; instrumento: number };
};
export type SubFamilyKey = keyof SubState;

/** Plantilla de indicadores: override de banda por entrada de catálogo. */
export type BandOverrides = Record<string, Record<string, RiskNivel>>;
export const BAND_OVERRIDES_KEY = 'cumpli.matriz.bandOverrides.v1';
/** Indicadores deshabilitados por tipo/catKey (vacío = todo habilitado). */
export type DisabledIndicators = Partial<Record<EntityMode, Record<string, string[]>>>;
export const ENABLED_INDICATORS_KEY = 'cumpli.matriz.enabledIndicators.v1';

export type SectorPreset = ReturnType<typeof sectorPresetsForMode>[number];

/** All the matrix state + actions, shared across the two tabs. */
export interface MatrixController {
  isLoading: boolean;
  isError: boolean;
  savedMatrix: RiskMatrixResponse | undefined;
  version: number;

  canEdit: boolean;
  canSwitchMode: boolean;

  mode: EntityMode;
  switchMode: (next: EntityMode) => void;
  switching: boolean;

  weights: Weights;
  thresholds: Thresholds;
  subweights: SubState;
  setWeight: (key: keyof Weights, value: number) => void;
  setSub: (family: SubFamilyKey, key: string, value: number) => void;
  setThresholds: Dispatch<SetStateAction<Thresholds>>;
  applyPreset: (preset: RiskMatrixConfig) => void;

  bandOverrides: BandOverrides;
  setBandOverrides: (next: BandOverrides) => void;
  disabledForMode: Record<string, string[]>;
  setDisabledForMode: (next: Record<string, string[]>) => void;
  userDisabled: { families: RiskFactorKey[]; subfactors: string[] };

  matrix: RiskMatrixConfig;
  effectiveMatrix: RiskMatrixConfig;

  total: number;
  sumOk: boolean;
  thresholdsOk: boolean;
  subSumsOk: boolean;
  canSave: boolean;
  dirty: boolean;

  save: () => void;
  saving: boolean;
  resetToSaved: () => void;

  /** Configuración tab state machine: saved-view (false) ⇄ wizard (true). A matrix
   *  with version 0 always shows the wizard regardless of this flag. */
  editing: boolean;
  startEdit: () => void;
  /** Discard unsaved wizard edits (weights + template) and return to the saved view. */
  cancelEdit: () => void;

  modePresets: SectorPreset[];
  activePreset: 'recomendado' | 'preliminar' | null;
  activeSector: SectorPreset | undefined;
}

// -------------------------------------------------------------- constants ----

/** Families that carry sub-factors, in display order. producto is single-variable. */
export const SUB_FAMILIES: SubFamilyKey[] = ['cliente', 'jurisdiccion', 'canal'];

export const FACTORS: { key: keyof Weights; label: string; aka?: string; hint: string }[] = [
  { key: 'cliente', label: 'Cliente', hint: 'Profesión, estructura jurídica, antecedentes' },
  { key: 'jurisdiccion', label: 'Jurisdicción', aka: 'Geografía', hint: 'Nacionalidad, fondos, domicilio, listas internacionales' },
  { key: 'producto', label: 'Producto', hint: 'Producto o servicio contratado (variable única)' },
  { key: 'canal', label: 'Canal', aka: 'Transaccionalidad', hint: 'Vía, frecuencia, monto, instrumento' },
];

export const NIVEL_TONE: Record<RiskNivel, 'green' | 'amber' | 'red'> = { bajo: 'green', medio: 'amber', alto: 'red' };
export const NIVELES: RiskNivel[] = ['bajo', 'medio', 'alto'];

/** es-PA copy for the firm mode (§2) — from the shared catalog, never inline. */
export const ENTITY_MODE_COPY = esPA.entityMode;

/** The firm modes, with their catalog label + icon. */
export const ENTITY_MODES: { key: EntityMode; label: string; icon: typeof Scale }[] = [
  { key: 'financiero', label: ENTITY_MODE_COPY.financiero, icon: Landmark },
  { key: 'abogados', label: ENTITY_MODE_COPY.abogados, icon: Scale },
  { key: 'valores', label: ENTITY_MODE_COPY.valores, icon: TrendingUp },
  { key: 'seguros', label: ENTITY_MODE_COPY.seguros, icon: Umbrella },
  { key: 'inmobiliaria', label: ENTITY_MODE_COPY.inmobiliaria, icon: Building2 },
];

/** Nota es-PA para un factor/sub-factor que el usuario apagó en su plantilla. */
export const USER_OFF_NOTE =
  'Desactivado en su plantilla — se re-normaliza fuera del puntaje. Actívelo en el paso Avanzado.';

/** Cuántos indicadores se muestran de entrada por sub-factor. */
export const EDITOR_PAGE = 8;

/** Short row labels for the simulator; the full sub-factor name stays in the tooltip. */
export const SIM_ROW_LABELS: Record<string, string> = {
  'cliente.profesion': 'Profesión',
  'cliente.estructura': 'Estructura',
  'cliente.antecedentes': 'Antecedentes',
  'jurisdiccion.nacionalidad': 'Nacionalidad',
  'jurisdiccion.origen_destino': 'Fondos',
  'jurisdiccion.domicilio': 'Domicilio',
  'jurisdiccion.listas': 'Listas',
  producto: 'Producto',
  'canal.canal_via': 'Vía',
  'canal.frecuencia': 'Frecuencia',
  'canal.monto': 'Monto',
  'canal.instrumento': 'Instrumento',
};

// ---------------------------------------------------------------- helpers ----

export const sum = (o: Record<string, number>): number => Object.values(o).reduce((a, b) => a + b, 0);

/** Sub-weights of a matrix, falling back to the recommended defaults per family. */
export function subOf(cfg: RiskMatrixConfig | RiskMatrixResponse): SubState {
  const s = cfg.subweights;
  const rec = RECOMMENDED_RISK_MATRIX.subweights!;
  return {
    cliente: s?.cliente ?? rec.cliente!,
    jurisdiccion: s?.jurisdiccion ?? rec.jurisdiccion!,
    canal: s?.canal ?? rec.canal!,
  };
}

/** Aplica los overrides de banda a un catálogo, devolviendo entradas efectivas. */
export function applyBandOverrides(
  entries: readonly RiskCatalogEntry[],
  ov: Record<string, RiskNivel> | undefined
): RiskCatalogEntry[] {
  if (!ov) return entries as RiskCatalogEntry[];
  return entries.map((e) => (ov[e.key] && ov[e.key] !== e.band ? { ...e, band: ov[e.key]! } : e));
}

/** Whether a whole family participates in the weighted set for this mode. */
export function familyAppliesInMode(mode: EntityMode, family: RiskFactorKey): boolean {
  return INDICATOR_APPLICABILITY[mode].families.includes(family);
}

/** Whether a `family.subfactor` leaf is active (family on + not in disabled set). */
export function subfactorAppliesInMode(mode: EntityMode, family: RiskFactorKey, sub: string): boolean {
  const a = INDICATOR_APPLICABILITY[mode];
  return a.families.includes(family) && !a.disabledSubfactors.includes(`${family}.${sub}`);
}

/**
 * Deriva, del set de indicadores apagados por catKey, qué sub-factores y familias
 * quedan COMPLETAMENTE fuera (todos sus indicadores del tipo apagados) — eso es lo
 * que `effectiveMatrixForMode` re-normaliza.
 */
export function deriveUserDisabled(
  mode: EntityMode,
  disabled: Record<string, string[]>
): { families: RiskFactorKey[]; subfactors: string[] } {
  const families: RiskFactorKey[] = [];
  const subfactors: string[] = [];
  const allOff = (catKey: string): boolean => {
    const base = catalogForMode(RISK_SUBFACTOR_CATALOGS[catKey] ?? [], mode);
    const off = new Set(disabled[catKey] ?? []);
    return base.length > 0 && base.every((e) => off.has(e.key));
  };
  for (const fam of RISK_FACTOR_REFERENCE) {
    if (!familyAppliesInMode(mode, fam.key)) continue;
    if (fam.subfactors.length === 0) {
      if (allOff('producto')) families.push(fam.key); // producto (variable única)
      continue;
    }
    const activeSubs = fam.subfactors.filter((sf) => subfactorAppliesInMode(mode, fam.key, sf.key));
    const offSubs = activeSubs.filter((sf) => allOff(`${fam.key}.${sf.key}`));
    if (activeSubs.length > 0 && offSubs.length === activeSubs.length) {
      families.push(fam.key); // toda la familia fuera → cae entera
    } else {
      for (const sf of offSubs) subfactors.push(`${fam.key}.${sf.key}`);
    }
  }
  return { families, subfactors };
}

/** Accent/case-insensitive fold for combobox filters. */
export const foldSearch = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// ------------------------------------------------------------- WizardStep ----

/**
 * QuestionScreen clone for the guided config wizard, but with an `<h2>` instead of
 * the flow primitive's `<h1>` — the console PageHeader already owns the single page
 * `<h1>` ("Matriz de riesgo"), and the a11y regression test asserts exactly one h1
 * on this route. Same visual language as the intake journey.
 */
export function WizardStep({
  step,
  question,
  helper,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  secondary,
}: {
  step?: string;
  question: string;
  helper?: string;
  children?: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  secondary?: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1">
        {step && (
          <div className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-faint">
            {step}
          </div>
        )}
        <h2 className="font-serif text-3xl font-medium leading-tight text-ink sm:text-4xl">{question}</h2>
        {helper && <p className="mt-3 text-base text-ink-soft">{helper}</p>}
        {children && <div className="mt-8">{children}</div>}
      </div>
      <div className="sticky bottom-0 mt-8 space-y-3 bg-gradient-to-t from-canvas via-canvas pt-4">
        <BigButton onClick={onPrimary} disabled={primaryDisabled}>
          {primaryLabel}
        </BigButton>
        {secondary}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- WeightRow ----

/** Explanation text for a factor/sub-factor's ⓘ — plain-language, journey style. */
export function explainOf(ref: { explicacion: string; fundamento: string } | undefined): string | undefined {
  if (!ref) return undefined;
  return `${ref.explicacion} · Fundamento: ${ref.fundamento}`;
}

/** Weight slider + number input, with an optional ⓘ help and mode/off badge. */
export function WeightRow({
  label,
  aka,
  hint,
  help,
  value,
  disabled,
  small,
  badge,
  onChange,
}: {
  label: string;
  aka?: string | undefined;
  hint?: string | undefined;
  help?: string | undefined;
  value: number;
  disabled: boolean;
  small?: boolean | undefined;
  badge?: string | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <Label>
        {label}
        {aka ? <span className="ml-1 text-xs font-normal text-ink-faint">· {aka}</span> : null}
        {badge ? (
          <span className="ml-1.5 inline-block rounded-full bg-line px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            {badge}
          </span>
        ) : null}
        {help ? (
          <span className="ml-1 inline-flex align-middle">
            <FieldHelp text={help} label={`Qué significa: ${label}`} />
          </span>
        ) : null}
      </Label>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-line-strong accent-primary disabled:cursor-not-allowed"
        />
        <div className="flex w-20 items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
            className={cn('text-right', small ? 'h-8' : 'h-9')}
          />
          <span className="text-sm text-ink-faint">%</span>
        </div>
      </div>
      {hint ? <p className="mt-1 text-xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}

// -------------------------------------------------------------- LevelPicker ----

/** Segmented bajo/medio/alto picker. */
export function LevelPicker({ value, onChange }: { value: RiskNivel; onChange: (v: RiskNivel) => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-line">
      {NIVELES.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={cn(
            'px-2.5 py-1 text-xs font-semibold capitalize transition-colors',
            value === n ? 'text-white' : 'bg-panel text-ink-faint hover:text-ink'
          )}
          style={
            value === n
              ? { background: n === 'bajo' ? THRESHOLD_HEX.bajo : n === 'medio' ? THRESHOLD_HEX.medio : THRESHOLD_HEX.alto }
              : undefined
          }
        >
          {n}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------- ThresholdDial ----

/**
 * Conic donut + legend for the risk thresholds (0–100 split into bajo/medio/alto).
 * Shared by the wizard's Umbrales step and the saved-matrix summary view.
 */
export function ThresholdDial({ thresholds }: { thresholds: Thresholds }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        aria-label={`Zonas de riesgo: bajo hasta ${thresholds.medio}, medio hasta ${thresholds.alto}, alto desde ${thresholds.alto}`}
        role="img"
        className="relative shrink-0"
        style={{ width: 96, height: 96 }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(
              ${THRESHOLD_HEX.bajo} 0% ${thresholds.medio}%,
              ${THRESHOLD_HEX.medio} ${thresholds.medio}% ${thresholds.alto}%,
              ${THRESHOLD_HEX.alto} ${thresholds.alto}% 100%
            )`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center justify-center rounded-full bg-panel" style={{ width: 56, height: 56 }}>
            <span className="font-mono text-[9px] leading-none text-ink-faint">0–100</span>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-wrap justify-center gap-x-4 gap-y-1 font-mono text-[10px] text-ink-faint">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: THRESHOLD_HEX.bajo }} />
          bajo &lt;{thresholds.medio}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: THRESHOLD_HEX.medio }} />
          medio {thresholds.medio}–{thresholds.alto}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: THRESHOLD_HEX.alto }} />
          alto ≥{thresholds.alto}
        </span>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- UsarToggle ----

/** Interruptor compacto "Usar / No usar" (es-PA) para (des)activar un indicador. */
export function UsarToggle({
  on,
  disabled,
  onToggle,
  label,
}: {
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? `Usar ${label} (clic para no usar)` : `No usar ${label} (clic para usar)`}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        on ? 'border-primary/40 bg-primary/10 text-primary' : 'border-line bg-canvas text-ink-faint'
      )}
    >
      {on ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <Ban className="h-3 w-3" strokeWidth={2.5} />}
      {on ? 'Usar' : 'No usar'}
    </button>
  );
}

// --------------------------------------------------------------- InfoBubble ----

/** Burbuja ⓘ reutilizable: botón que abre/cierra un popover con texto (fundamento). */
export function InfoBubble({
  text,
  label,
  align = 'right',
}: {
  text: string;
  label: string;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn('shrink-0 transition-colors', open ? 'text-primary' : 'text-ink-faint hover:text-primary')}
      >
        <Info className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <div
          className={cn(
            'absolute top-full z-20 mt-1 w-72 rounded-xl border border-line bg-panel p-3 text-xs leading-relaxed text-ink-soft shadow-lg',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------- CatalogSelect ----

/** Searchable combobox for long catalogs (países, profesiones). */
function SearchableCatalog({
  entries,
  current,
  onChange,
}: {
  entries: RiskCatalogEntry[];
  current: RiskCatalogEntry | undefined;
  onChange: (entry: RiskCatalogEntry) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const filtered = query.trim()
    ? entries.filter((e) => foldSearch(e.label).includes(foldSearch(query)))
    : entries;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function choose(entry: RiskCatalogEntry) {
    onChange(entry);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        value={open ? query : current?.label ?? ''}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(a + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const pick = filtered[active];
            if (pick) choose(pick);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder={current?.label ?? 'Buscar…'}
        className="h-9 w-full rounded-lg border border-line bg-panel pl-2.5 pr-8 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:outline-none"
      />
      <ChevronDown
        className={cn(
          'pointer-events-none absolute right-2.5 top-[18px] h-4 w-4 -translate-y-1/2 text-ink-faint transition-transform',
          open && 'rotate-180'
        )}
        strokeWidth={2}
      />
      {open && (
        <ul className="absolute z-40 mt-1 max-h-64 min-w-full w-max max-w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-line-strong bg-panel py-1 shadow-pop">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-xs text-ink-faint">Sin coincidencias</li>
          ) : (
            filtered.map((e, i) => (
              <li key={e.key}>
                <button
                  type="button"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    choose(e);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'flex w-full items-start gap-2 px-2.5 py-1.5 text-left text-sm transition-colors',
                    i === active ? 'bg-canvas' : 'hover:bg-canvas',
                    e.key === current?.key ? 'font-semibold text-primary-dark' : 'text-ink'
                  )}
                >
                  <Pill tone={NIVEL_TONE[e.band]} className="mt-0.5 shrink-0 capitalize">
                    {e.band}
                  </Pill>
                  <span className="min-w-0 flex-1 leading-snug">{e.label}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** Listbox custom para listas cortas de indicadores por sub-factor. */
function CatalogListbox({
  entries,
  value,
  onChange,
}: {
  entries: RiskCatalogEntry[];
  value: string;
  onChange: (entry: RiskCatalogEntry) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rid = useId();
  const listboxId = `${rid}-lb`;
  const triggerId = `${rid}-tr`;
  const optId = (key: string) => `${rid}-o-${key}`;
  const selected = entries.find((e) => e.key === value);

  function openList() {
    const sel = entries.findIndex((e) => e.key === value);
    setActive(sel >= 0 ? sel : 0);
    setOpen(true);
  }
  function choose(entry: RiskCatalogEntry) {
    onChange(entry);
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => {
    if (!open || active < 0) return;
    const e = entries[active];
    if (e) document.getElementById(optId(e.key))?.scrollIntoView({ block: 'nearest' });
  }, [open, active, entries]);

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openList();
          }
        }}
        className="flex h-9 w-full items-center rounded-lg border border-line bg-panel pl-2.5 pr-8 text-left text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        <span className="min-w-0 flex-1 truncate">{selected ? selected.label : '—'}</span>
      </button>
      <ChevronDown
        className={cn(
          'pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint transition-transform',
          open && 'rotate-180'
        )}
        strokeWidth={2}
      />
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={active >= 0 && entries[active] ? optId(entries[active]!.key) : undefined}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, entries.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Home') {
              e.preventDefault();
              setActive(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              setActive(entries.length - 1);
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const en = entries[active];
              if (en) choose(en);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
              triggerRef.current?.focus();
            } else if (e.key === 'Tab') {
              setOpen(false);
            }
          }}
          className="absolute z-40 mt-1 max-h-72 min-w-full w-max max-w-[min(24rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-line-strong bg-panel py-1 shadow-pop focus:outline-none"
        >
          {entries.map((en, idx) => {
            const isSel = en.key === value;
            const isActive = idx === active;
            return (
              <li
                key={en.key}
                id={optId(en.key)}
                role="option"
                aria-selected={isSel}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(en);
                }}
                onMouseEnter={() => setActive(idx)}
                className={cn(
                  'flex cursor-pointer items-start gap-2 px-2.5 py-1.5 text-sm transition-colors',
                  isActive ? 'bg-canvas' : 'hover:bg-canvas',
                  isSel ? 'font-semibold text-primary-dark' : 'text-ink'
                )}
              >
                <Check
                  className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', isSel ? 'text-primary' : 'opacity-0')}
                  strokeWidth={2.5}
                />
                <Pill tone={NIVEL_TONE[en.band]} className="mt-0.5 shrink-0 capitalize">
                  {en.band}
                </Pill>
                <span className="min-w-0 flex-1 leading-snug">{en.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Pick a concrete situation; the band derives from the documented mapping. */
export function CatalogSelect({
  entries,
  value,
  onChange,
}: {
  entries: RiskCatalogEntry[];
  value: string;
  onChange: (entry: RiskCatalogEntry) => void;
}) {
  const current = entries.find((e) => e.key === value);
  const searchable = entries.length > 12;
  return (
    <div className="min-w-0 flex-1">
      {searchable ? (
        <SearchableCatalog entries={entries} current={current} onChange={onChange} />
      ) : (
        <CatalogListbox entries={entries} value={value} onChange={onChange} />
      )}
      {current && (
        <div className="mt-1 flex items-center gap-2">
          <Pill tone={NIVEL_TONE[current.band]} className="shrink-0 capitalize">
            {current.band}
          </Pill>
          <InfoBubble text={current.fundamento} label={`Ver fundamento legal — ${current.label}`} align="left" />
        </div>
      )}
    </div>
  );
}
